// Name and label rules for the legacy Font Awesome markup shim
// (legacyFaIcons.js). Pure functions, no DOM: this file runs under plain
// Node as well as in the bundle, so every import carries its extension.
//
// Seven shared packages (@apexl/icarus-branding, -content-flows,
// -notifications, -taxonomy, -permissions, -views, -content-library) still
// write their icons as `<i class="fal fa-NAME"></i>`. Font Awesome was removed
// in the icarus-core 3.1 icon migration, so nothing draws that markup. The
// shim fills each such `<i>` with the Cygnus glyph for NAME, which is decided
// here:
//
//   1. resolveIconName(NAME): the Cygnus set has the name, or icarus-core's
//      alias table does.
//   2. Otherwise the project's own Font Awesome to Cygnus map
//      (scripts/iconMap.mjs, the map the 3.1 migration of this app used), so
//      a package icon and an app icon of the same Font Awesome name draw the
//      same glyph.
//   3. Otherwise EXTRA_FALLBACK below: names only the packages use.
//   4. Otherwise nothing is drawn (the `<i>` is left as it is).
import resolveIconName from '@apexl/icarus-core/src/services/icons/resolveIconName.mjs';
import { NAME_MAP } from '../../../scripts/iconMap.mjs';

// The tokens that mark an element as Font Awesome markup. The short forms and
// the Font Awesome 6 long forms (icarus-views writes `fa-solid fa-NAME`).
export const STYLE_TOKENS = [
  'fa', 'fal', 'far', 'fas', 'fab', 'fad', 'fat',
  'fa-light', 'fa-regular', 'fa-solid', 'fa-brands', 'fa-duotone', 'fa-thin',
];
const STYLE_SET = new Set(STYLE_TOKENS);
const SOLID_SET = new Set(['fas', 'fa-solid']);
const SPIN_SET = new Set(['fa-spin', 'fa-pulse', 'fa-spin-pulse']);

// `fa-*` tokens that change how an icon is drawn and are not an icon name.
const MODIFIER = /^fa-(fw|spin|spin-pulse|spin-reverse|pulse|lg|sm|xs|xl|2xs|2xl|border|inverse|fixed-width|stack|stack-1x|stack-2x|beat|beat-fade|fade|flip|flip-horizontal|flip-vertical|flip-both|shake|bounce|li|ul|rotate-\d+|pull-left|pull-right|\d+x)$/;
const NAME_TOKEN = /^fa-[a-z0-9]+(-[a-z0-9]+)*$/;

// Font Awesome names the packages use that neither icarus-core nor
// scripts/iconMap.mjs knows: the nearest glyph of the Cygnus set.
export const EXTRA_FALLBACK = {
  'arrow-right-long': 'arrow-right',
  'arrow-rotate-right': 'refresh',
  'arrow-turn-down-right': 'arrow-right',
  'book-open': 'book',
  calculator: 'chart-bar',
  'calendar-days': 'calendar',
  'circle-question': 'question',
  cube: 'box',
  'file-plus': 'file',
  'folder-tree': 'folder',
  handshake: 'hand',
  heading: 'newspaper',
  headset: 'phone',
  merge: 'shuffle',
  'mobile-screen': 'mobile',
  'object-group': 'layer-group',
  paragraph: 'list',
  'rectangle-list': 'list',
  'ruler-combined': 'gauge',
  save: 'download',
  'square-plus': 'plus',
  swatchbook: 'palette',
  'table-layout': 'table',
  ticket: 'note-sticky',
  unlock: 'x',
  'user-tie': 'user',
};

// The label of a button whose only content is the icon. A function gets
// `{ danger }`: whether the button is styled as a destructive action.
const closeOrRemove = ({ danger }) => (danger ? 'Remove' : 'Close');
export const LABELS = {
  'arrow-up': 'Move up',
  'arrow-down': 'Move down',
  trash: 'Delete',
  desktop: 'Desktop preview',
  mobile: 'Mobile preview',
  xmark: closeOrRemove,
  times: closeOrRemove,
  pen: 'Edit',
  pencil: 'Edit',
  plus: 'Add',
  lock: 'Lock',
  unlock: 'Unlock',
  cog: 'Settings',
  gear: 'Settings',
  'chevron-left': 'Previous',
  'chevron-right': 'Next',
  'rotate-left': 'Reset',
  rotate: 'Refresh',
  'arrow-rotate-right': 'Refresh',
  'heart-pulse': 'Check health',
  'box-archive': 'Archive',
  eye: 'Show',
  'eye-slash': 'Hide',
  copy: 'Copy',
  download: 'Download',
  upload: 'Upload',
  'magnifying-glass': 'Search',
  'paper-plane': 'Send',
  merge: 'Merge',
  save: 'Save',
  'floppy-disk': 'Save',
};

/**
 * Read a class attribute as Font Awesome markup.
 *
 * It is Font Awesome markup only when it has a style token (`fa`, `fal`,
 * `far`, `fas`, `fab`, `fad`, `fat` or a long form such as `fa-solid`) AND an
 * `fa-NAME` token that is neither a style token nor a modifier (`fa-spin`,
 * `fa-fw`, `fa-2x` ...). Anything else returns null and is left alone.
 *
 * @param {string} className
 * @returns {{name: string, variant: 'light'|'solid', spin: boolean}|null}
 */
export function parseFaClass(className) {
  if (typeof className !== 'string' || className.indexOf('fa') === -1) return null;
  const tokens = className.split(/\s+/).filter(Boolean);
  let styled = false;
  let solid = false;
  let spin = false;
  let name = null;
  for (const token of tokens) {
    if (STYLE_SET.has(token)) {
      styled = true;
      if (SOLID_SET.has(token)) solid = true;
    } else if (SPIN_SET.has(token)) {
      spin = true;
    } else if (name === null && NAME_TOKEN.test(token) && !MODIFIER.test(token)) {
      name = token.slice(3);
    }
  }
  if (!styled || name === null) return null;
  return { name, variant: solid ? 'solid' : 'light', spin };
}

/**
 * The Cygnus icon drawn for a Font Awesome name, or null when there is none.
 *
 * @param {string} faName  bare name, no `fa-` prefix
 * @returns {string|null}
 */
export function cygnusIconFor(faName) {
  if (!faName || typeof faName !== 'string') return null;
  const direct = resolveIconName(faName);
  if (direct) return direct;
  const mapped = Object.prototype.hasOwnProperty.call(NAME_MAP, faName)
    ? NAME_MAP[faName]
    : (Object.prototype.hasOwnProperty.call(EXTRA_FALLBACK, faName) ? EXTRA_FALLBACK[faName] : null);
  return mapped ? resolveIconName(mapped) : null;
}

/**
 * The label for an icon-only button showing this Font Awesome icon, or null
 * when the icon does not say what the button does.
 *
 * @param {string} faName
 * @param {{danger?: boolean}} [context]
 * @returns {string|null}
 */
export function labelFor(faName, context = {}) {
  if (!Object.prototype.hasOwnProperty.call(LABELS, faName)) return null;
  const label = LABELS[faName];
  return typeof label === 'function' ? label({ danger: !!context.danger }) : label;
}

/**
 * Whether a label may be written onto a button: only when the button has no
 * text of its own and no label that somebody else gave it. `ours` is true
 * when the shim wrote the current label itself (so it may replace it when the
 * icon changes).
 *
 * @param {{text: string, title: ?string, ariaLabel: ?string, ariaLabelledBy: ?string, ours: boolean}} button
 * @returns {boolean}
 */
export function mayLabel(button) {
  if ((button.text || '').trim() !== '') return false;
  if (button.ours) return true;
  return !button.title && !button.ariaLabel && !button.ariaLabelledBy;
}
